export const agentCategories = [
  {
    id: 'cat-customer-support',
    slug: 'customer-support',
    name: 'Customer Support',
    description: 'Agents that resolve tickets, answer questions, and recover churn.',
  },
  {
    id: 'cat-sales-growth',
    slug: 'sales-growth',
    name: 'Sales & Growth',
    description: 'Agents for outbound prospecting, lead qualification, funnel optimization, and revenue workflows.',
  },
  {
    id: 'cat-operations',
    slug: 'operations',
    name: 'Operations',
    description: 'Agents that automate back-office workflows and reporting.',
  },
  {
    id: 'cat-marketing-content',
    slug: 'marketing-content',
    name: 'Marketing & Content',
    description: 'Agents for campaign production, content creation, repurposing, and publishing workflows.',
  },
  {
    id: 'cat-research',
    slug: 'research',
    name: 'Research',
    description: 'Market intelligence, competitive teardown, and lead enrichment agents.',
  },
  {
    id: 'cat-data-spreadsheets',
    slug: 'data-spreadsheets',
    name: 'Data & Spreadsheets',
    description: 'Agents that clean data, update sheets, build reports, and automate spreadsheet workflows.',
  },
  {
    id: 'cat-software-automation',
    slug: 'software-automation',
    name: 'Software & Automation',
    description: 'Agents for engineering workflows, integrations, QA, scripts, and business process automation.',
  },
];

export const marketplaceFeed = [
  {
    id: 'feed-1',
    seller: 'Lane @ Signal Forge',
    title: 'Shipped a new escalation policy for Shopify refunds',
    detail: 'The support agent now resolves 68% of refund tickets without a human handoff.',
  },
  {
    id: 'feed-2',
    seller: 'Mika @ DemandPilot',
    title: 'Added Apollo enrichment + Loom summaries',
    detail: 'Buyers can now hire the SDR stack with 3 clicks and route outputs into HubSpot.',
  },
  {
    id: 'feed-3',
    seller: 'June @ LedgerFlow Ops',
    title: 'Publishing template added for agency finance teams',
    detail: 'The monthly close workflow now exports reconciled reports directly into Slack.',
  },
];

export const promoSpots = [
  {
    id: 'promo-1',
    name: 'Inbox Reactor',
    line: 'AI SDR that books demos from cold email and LinkedIn.',
    tone: 'blue',
  },
  {
    id: 'promo-2',
    name: 'Ticket Triage',
    line: 'Support copilot for SaaS teams drowning in tickets.',
    tone: 'graphite',
  },
  {
    id: 'promo-3',
    name: 'Clip Mill',
    line: 'Repurpose long-form video into short-form content at scale.',
    tone: 'plum',
  },
  {
    id: 'promo-4',
    name: 'Pipeline Ghost',
    line: 'Prospecting stack that researches, scores, and drafts outreach.',
    tone: 'burgundy',
  },
  {
    id: 'promo-5',
    name: 'Ops Ledger',
    line: 'Finance ops agent for reconciliation and close reporting.',
    tone: 'blue',
  },
];

export const agentListings = [
  {
    id: 'listing-signal-forge',
    slug: 'signal-forge-support',
    title: 'Signal Forge Support',
    tagline: 'Deploy a customer support agent that resolves tickets, updates CRMs, and flags churn risk.',
    summary:
      'Built for SaaS teams that need a reliable AI frontline across email, chat, and help center traffic.',
    description:
      'Signal Forge combines retrieval, sentiment analysis, escalation rules, and CRM actions into one support operator. It is sold as a managed agent listing, with setup, prompt tuning, and weekly optimization included.',
    categoryId: 'cat-customer-support',
    priceFrom: 320,
    deliveryDays: 7,
    revisionCount: 3,
    featured: true,
    verified: true,
    instantHire: true,
    createdAt: '2026-04-18T10:00:00.000Z',
    responseHours: 2,
    ordersCompleted: 38,
    successRate: 96,
    rating: 4.9,
    reviewCount: 14,
    seller: {
      id: 'seller-lane',
      name: 'Lane Mercer',
      headline: 'Support systems operator for B2B SaaS',
      location: 'Austin, TX',
      memberSince: '2024-02-01T00:00:00.000Z',
      responseRate: 98,
      completionRate: 97,
      about:
        'I productize AI agents that sit inside existing support stacks without forcing a replatform.',
    },
    stack: ['OpenAI', 'Supabase', 'Zendesk', 'Slack'],
    industries: ['B2B SaaS', 'Marketplace', 'Agencies'],
    outcomes: ['Lower first-response time', 'Automate refund handling', 'Escalate churn signals'],
    useCases: ['Ticket triage', 'Refund eligibility checks', 'KB answer generation'],
    featuredQuote: 'Cut the support queue before you hire another tier-one rep.',
    tone: 'blue',
    packages: [
      {
        tier: 'Starter',
        name: 'Ticket Deflection',
        price: 320,
        description: 'Launch on one support channel with retrieval and escalation rules.',
        deliveryDays: 7,
        revisions: 2,
        features: ['1 support inbox', 'Knowledge base ingestion', 'Escalation routing'],
      },
      {
        tier: 'Growth',
        name: 'Multi-Channel Support',
        price: 420,
        description: 'Email plus chat coverage with CRM actions and analytics.',
        deliveryDays: 10,
        revisions: 3,
        features: ['2 channels', 'CRM updates', 'CSAT tagging', 'Weekly optimization'],
      },
      {
        tier: 'Scale',
        name: 'Managed Support Operator',
        price: 500,
        description: 'Fully managed support agent with custom workflows and QA reviews.',
        deliveryDays: 14,
        revisions: 4,
        features: ['Unlimited intents', 'Escalation map', 'QA dashboard', 'Dedicated tuning'],
      },
    ],
    faqs: [
      {
        question: 'Do I need an existing help center?',
        answer: 'Yes. The fastest launches happen when your docs and prior ticket history already exist.',
      },
      {
        question: 'Can it hand off to a human?',
        answer: 'Yes. Handoffs can route by sentiment, confidence score, account tier, or custom rules.',
      },
    ],
    reviews: [
      {
        id: 'review-1',
        reviewerName: 'Alyssa Trent',
        company: 'ArcLayer',
        rating: 5,
        body: 'We dropped our first response time from 3.2 hours to 17 minutes in the first week.',
      },
      {
        id: 'review-2',
        reviewerName: 'Derek Long',
        company: 'ProofKit',
        rating: 5,
        body: 'The escalation logic was the difference. It felt like a real operator, not a chat widget.',
      },
    ],
  },
  {
    id: 'listing-demandpilot',
    slug: 'demandpilot-outbound',
    title: 'DemandPilot Outbound',
    tagline: 'Prospecting agent that researches ICPs, scores leads, and drafts outbound sequences.',
    summary: 'A managed AI SDR engine for agencies, SaaS founders, and high-ticket service teams.',
    description:
      'DemandPilot handles lead research, enrichment, personalization, and outbound drafting across LinkedIn and email. Buyers receive a hire-ready agent with dashboards and weekly performance reviews.',
    categoryId: 'cat-sales-growth',
    priceFrom: 150,
    deliveryDays: 5,
    revisionCount: 2,
    featured: true,
    verified: true,
    instantHire: true,
    createdAt: '2026-04-20T14:20:00.000Z',
    responseHours: 1,
    ordersCompleted: 52,
    successRate: 94,
    rating: 4.8,
    reviewCount: 21,
    seller: {
      id: 'seller-mika',
      name: 'Mika Doyle',
      headline: 'Revenue systems builder for lean growth teams',
      location: 'Chicago, IL',
      memberSince: '2023-09-01T00:00:00.000Z',
      responseRate: 99,
      completionRate: 95,
      about:
        'I build outbound systems that marry research quality with ruthless throughput, without bloated agency retainers.',
    },
    stack: ['OpenAI', 'Clay', 'Apollo', 'HubSpot'],
    industries: ['SaaS', 'Recruiting', 'Agencies'],
    outcomes: ['Faster list building', 'Higher email relevance', 'Cleaner CRM handoff'],
    useCases: ['Outbound list generation', 'Persona research', 'Email draft creation'],
    featuredQuote: 'Hire the agent that ships outbound like a niche growth team, not a generic VA.',
    tone: 'plum',
    packages: [
      {
        tier: 'Starter',
        name: 'Lead Research Sprint',
        price: 150,
        description: 'ICP map, list sourcing, and draft sequences for one campaign.',
        deliveryDays: 5,
        revisions: 1,
        features: ['250 leads', '2 personas', '3-sequence draft'],
      },
      {
        tier: 'Growth',
        name: 'Outbound Engine',
        price: 225,
        description: 'Fully managed research and outbound drafting across two channels.',
        deliveryDays: 7,
        revisions: 2,
        features: ['750 leads', 'LinkedIn messaging', 'CRM sync', 'Reply labeling'],
      },
      {
        tier: 'Scale',
        name: 'Pipeline Machine',
        price: 350,
        description: 'Ongoing outbound operations with weekly tuning and reporting.',
        deliveryDays: 12,
        revisions: 3,
        features: ['Multi-ICP campaigns', 'A/B variants', 'Weekly reporting', 'Playbook docs'],
      },
    ],
    faqs: [
      {
        question: 'Will you send the emails too?',
        answer: 'Yes, if you connect your sending stack. Otherwise I deliver sequences and workflows.',
      },
    ],
    reviews: [
      {
        id: 'review-3',
        reviewerName: 'Mona Singh',
        company: 'Northcamp',
        rating: 5,
        body: 'The targeting quality was miles ahead of the generic outbound shops we tested.',
      },
    ],
  },
  {
    id: 'listing-ledgerflow',
    slug: 'ledgerflow-close-agent',
    title: 'LedgerFlow Close Agent',
    tagline: 'Ops agent that reconciles transactions, prepares close summaries, and pushes reports to Slack.',
    summary: 'Designed for agencies and SaaS finance teams that need a dependable month-end workflow.',
    description:
      'LedgerFlow turns recurring finance operations into a managed AI workflow with human-review checkpoints. It reads exports, flags anomalies, and assembles executive-ready summaries.',
    categoryId: 'cat-operations',
    priceFrom: 520,
    deliveryDays: 9,
    revisionCount: 2,
    featured: true,
    verified: true,
    instantHire: false,
    createdAt: '2026-04-22T09:15:00.000Z',
    responseHours: 4,
    ordersCompleted: 17,
    successRate: 98,
    rating: 4.9,
    reviewCount: 8,
    seller: {
      id: 'seller-june',
      name: 'June Holloway',
      headline: 'Finance ops systems architect',
      location: 'New York, NY',
      memberSince: '2024-05-01T00:00:00.000Z',
      responseRate: 95,
      completionRate: 98,
      about:
        'I build AI-assisted finance operations for teams that want leverage without risking sloppy close processes.',
    },
    stack: ['OpenAI', 'Supabase', 'Google Sheets', 'Slack'],
    industries: ['Agencies', 'SaaS', 'E-commerce'],
    outcomes: ['Faster monthly close', 'Anomaly detection', 'Executive reporting'],
    useCases: ['Reconciliation review', 'Variance alerts', 'Close summary generation'],
    featuredQuote: 'Your monthly close should feel like a system, not a scramble.',
    tone: 'graphite',
    packages: [
      {
        tier: 'Starter',
        name: 'Close Assistant',
        price: 520,
        description: 'One workflow for reconciliation and close summary generation.',
        deliveryDays: 9,
        revisions: 2,
        features: ['1 reporting workflow', 'Variance flags', 'Slack summary'],
      },
      {
        tier: 'Growth',
        name: 'Finance Ops Layer',
        price: 780,
        description: 'Multi-step monthly close system with review checkpoints.',
        deliveryDays: 12,
        revisions: 3,
        features: ['2 workflows', 'Reviewer checkpoints', 'Historical comparisons'],
      },
      {
        tier: 'Scale',
        name: 'Managed Finance Automation',
        price: 1200,
        description: 'Custom finance agent network for lean operations teams.',
        deliveryDays: 18,
        revisions: 4,
        features: ['Unlimited workflows', 'Dashboard buildout', 'Weekly reporting'],
      },
    ],
    faqs: [],
    reviews: [
      {
        id: 'review-4',
        reviewerName: 'Ishan Patel',
        company: 'Pilotform',
        rating: 5,
        body: 'The final output looked like a controller wrote it, but the process ran in hours.',
      },
    ],
  },
  {
    id: 'listing-briefloom',
    slug: 'briefloom-content-engine',
    title: 'Briefloom Content Engine',
    tagline: 'Agentic content system for research, briefs, draft writing, and repurposing.',
    summary: 'A plug-in editorial engine for lean marketing teams that still need publishing velocity.',
    description:
      'Briefloom turns an editorial backlog into a repeatable pipeline. It clusters topics, drafts content, repurposes it into social and newsletters, and keeps your content team moving.',
    categoryId: 'cat-marketing-content',
    priceFrom: 95,
    deliveryDays: 4,
    revisionCount: 2,
    featured: false,
    verified: true,
    instantHire: true,
    createdAt: '2026-04-16T12:30:00.000Z',
    responseHours: 2,
    ordersCompleted: 61,
    successRate: 93,
    rating: 4.7,
    reviewCount: 26,
    seller: {
      id: 'seller-sora',
      name: 'Sora Cain',
      headline: 'Content systems strategist',
      location: 'Remote',
      memberSince: '2023-01-10T00:00:00.000Z',
      responseRate: 97,
      completionRate: 94,
      about:
        'I specialize in editorial systems that turn founder knowledge into consistent multi-format content.',
    },
    stack: ['OpenAI', 'Notion', 'Zapier', 'Webflow'],
    industries: ['SaaS', 'Media', 'Consulting'],
    outcomes: ['Faster publishing cadence', 'Better content repurposing', 'Clear editorial briefs'],
    useCases: ['Blog production', 'Newsletter repurposing', 'SEO brief generation'],
    featuredQuote: 'Stop hiring one-off writers when what you need is a content machine.',
    tone: 'burgundy',
    packages: [
      {
        tier: 'Starter',
        name: 'Weekly Editorial Engine',
        price: 95,
        description: 'Topic clustering plus one editorial workflow for a weekly publish cadence.',
        deliveryDays: 4,
        revisions: 1,
        features: ['Topic map', 'Brief template', 'Repurposing prompts'],
      },
      {
        tier: 'Growth',
        name: 'Multi-Format Content',
        price: 165,
        description: 'Add social, newsletter, and repurposing workflows to the core engine.',
        deliveryDays: 7,
        revisions: 2,
        features: ['Blog + social', 'Newsletter version', 'Content review checklist'],
      },
      {
        tier: 'Scale',
        name: 'Managed Editorial Ops',
        price: 280,
        description: 'Ongoing content operations for fast-moving founder brands.',
        deliveryDays: 10,
        revisions: 3,
        features: ['Editorial calendar', 'Publishing ops', 'Weekly refinement'],
      },
    ],
    faqs: [],
    reviews: [],
  },
  {
    id: 'listing-proofswitch',
    slug: 'proofswitch-research-agent',
    title: 'Proofswitch Research',
    tagline: 'Research agent for competitive analysis, account plans, and market teardown briefs.',
    summary: 'Perfect for founders, revenue teams, and consultants who need clean intelligence fast.',
    description:
      'Proofswitch watches competitors, assembles market snapshots, and generates brief-ready research packages. It is sold as a managed listing with custom prompt stacks and delivery templates.',
    categoryId: 'cat-research',
    priceFrom: 45,
    deliveryDays: 3,
    revisionCount: 2,
    featured: false,
    verified: true,
    instantHire: true,
    createdAt: '2026-04-21T08:00:00.000Z',
    responseHours: 3,
    ordersCompleted: 29,
    successRate: 95,
    rating: 4.8,
    reviewCount: 11,
    seller: {
      id: 'seller-remy',
      name: 'Remy Thorne',
      headline: 'Competitive intelligence operator',
      location: 'Denver, CO',
      memberSince: '2024-01-20T00:00:00.000Z',
      responseRate: 96,
      completionRate: 96,
      about:
        'I package research agents that turn noisy markets into sharp decision-making inputs for operators.',
    },
    stack: ['OpenAI', 'Perplexity', 'Airtable', 'Slack'],
    industries: ['SaaS', 'Fintech', 'Agencies'],
    outcomes: ['Sharper account plans', 'Cleaner competitor snapshots', 'Faster research cycles'],
    useCases: ['Competitor teardowns', 'Market mapping', 'Account research'],
    featuredQuote: 'Research should sharpen a decision, not bury a team in tabs.',
    tone: 'graphite',
    packages: [
      {
        tier: 'Starter',
        name: 'Teardown Pack',
        price: 45,
        description: 'One complete competitor or market teardown workflow.',
        deliveryDays: 3,
        revisions: 1,
        features: ['1 market brief', 'Template outputs', 'Source list'],
      },
      {
        tier: 'Growth',
        name: 'Research Desk',
        price: 90,
        description: 'A recurring research system for GTM or founder workflows.',
        deliveryDays: 6,
        revisions: 2,
        features: ['3 templates', 'Weekly refresh', 'Slack summaries'],
      },
      {
        tier: 'Scale',
        name: 'Decision Intelligence',
        price: 180,
        description: 'Custom research engine with dashboards and recurring delivery.',
        deliveryDays: 10,
        revisions: 3,
        features: ['Custom dashboard', 'Source monitoring', 'Executive summary layer'],
      },
    ],
    faqs: [],
    reviews: [],
  },
  {
    id: 'listing-sheetpilot',
    slug: 'sheetpilot-reporting-assistant',
    title: 'SheetPilot Reporting Assistant',
    tagline: 'Spreadsheet agent that cleans raw exports, updates dashboards, and formats weekly reports.',
    summary: 'A fast-turn spreadsheet workflow for founders and operators who live in CSVs and Google Sheets.',
    description:
      'SheetPilot takes messy data exports and turns them into cleaned sheets, KPI views, and ready-to-share reporting tabs. It is built for teams that need spreadsheet automation without commissioning a larger systems project.',
    categoryId: 'cat-data-spreadsheets',
    priceFrom: 20,
    deliveryDays: 2,
    revisionCount: 1,
    featured: false,
    verified: true,
    instantHire: true,
    createdAt: '2026-04-19T11:10:00.000Z',
    responseHours: 2,
    ordersCompleted: 34,
    successRate: 97,
    rating: 4.8,
    reviewCount: 9,
    seller: {
      id: 'seller-ivy',
      name: 'Ivy Chen',
      headline: 'Spreadsheet automation specialist',
      location: 'San Diego, CA',
      memberSince: '2024-03-18T00:00:00.000Z',
      responseRate: 98,
      completionRate: 96,
      about:
        'I build lightweight spreadsheet systems for operators who want cleaner reporting without moving everything into a BI stack.',
    },
    stack: ['Google Sheets', 'Airtable', 'Zapier', 'OpenAI'],
    industries: ['SaaS', 'Agencies', 'E-commerce'],
    outcomes: ['Cleaner source data', 'Faster weekly reporting', 'Less spreadsheet cleanup'],
    useCases: ['CSV cleanup', 'Dashboard prep', 'Weekly KPI rollups'],
    featuredQuote: 'Turn a messy export into a clean reporting sheet in a single handoff.',
    tone: 'blue',
    packages: [
      {
        tier: 'Starter',
        name: 'Sheet Cleanup',
        price: 20,
        description: 'Clean one export and deliver a usable reporting sheet.',
        deliveryDays: 2,
        revisions: 1,
        features: ['1 source file', 'Column cleanup', 'Basic formulas'],
      },
      {
        tier: 'Growth',
        name: 'Reporting Layer',
        price: 45,
        description: 'Add KPI calculations and a simple weekly dashboard tab.',
        deliveryDays: 3,
        revisions: 1,
        features: ['Dashboard tab', 'KPI formulas', 'Formatting pass'],
      },
      {
        tier: 'Scale',
        name: 'Ops Spreadsheet System',
        price: 85,
        description: 'Connect multiple sheets into a repeatable reporting workflow.',
        deliveryDays: 5,
        revisions: 2,
        features: ['Multi-tab workbook', 'Automation notes', 'Template handoff'],
      },
    ],
    faqs: [],
    reviews: [],
  },
  {
    id: 'listing-bugsentinel',
    slug: 'bugsentinel-qa-agent',
    title: 'BugSentinel QA',
    tagline: 'QA agent that watches pull requests, drafts test plans, and files regression bugs.',
    summary: 'A developer productivity listing for teams that need more coverage without more headcount.',
    description:
      'BugSentinel plugs into your repo workflow, turns changes into risk-weighted test plans, and packages findings into engineering-friendly reports.',
    categoryId: 'cat-software-automation',
    priceFrom: 650,
    deliveryDays: 8,
    revisionCount: 2,
    featured: false,
    verified: true,
    instantHire: false,
    createdAt: '2026-04-15T15:45:00.000Z',
    responseHours: 5,
    ordersCompleted: 12,
    successRate: 92,
    rating: 4.7,
    reviewCount: 5,
    seller: {
      id: 'seller-noah',
      name: 'Noah Vale',
      headline: 'Engineering automation consultant',
      location: 'Seattle, WA',
      memberSince: '2024-06-12T00:00:00.000Z',
      responseRate: 90,
      completionRate: 92,
      about:
        'I turn repetitive QA and release tasks into agentic workflows that fit real engineering teams.',
    },
    stack: ['OpenAI', 'GitHub', 'Linear', 'Supabase'],
    industries: ['SaaS', 'Developer Tools'],
    outcomes: ['Higher test coverage', 'Faster PR review', 'Clearer release risk'],
    useCases: ['Regression test planning', 'Issue filing', 'Release readiness'],
    featuredQuote: 'Ship faster without pretending QA is optional.',
    tone: 'blue',
    packages: [
      {
        tier: 'Starter',
        name: 'PR Watcher',
        price: 650,
        description: 'One repository workflow for automated QA summaries.',
        deliveryDays: 8,
        revisions: 1,
        features: ['1 repo integration', 'PR summaries', 'Test checklist'],
      },
      {
        tier: 'Growth',
        name: 'Regression Layer',
        price: 950,
        description: 'Adds issue filing and release risk tagging to the workflow.',
        deliveryDays: 11,
        revisions: 2,
        features: ['Issue creation', 'Risk scoring', 'Release reports'],
      },
      {
        tier: 'Scale',
        name: 'Engineering QA System',
        price: 1450,
        description: 'Managed QA automation across multiple codebases or squads.',
        deliveryDays: 16,
        revisions: 3,
        features: ['Multi-repo setup', 'Alerting', 'Weekly QA review'],
      },
    ],
    faqs: [],
    reviews: [],
  },
];
